// src/utils/unitConversion.js

const METERS_TO_FEET = 3.28084;
const SQ_METERS_TO_SQ_FEET = 10.7639104;

/**
 * Convert meters to feet.
 * @param {number} m - meters
 * @returns {number} feet
 */
export const mToFt = (m) => {
    return m * METERS_TO_FEET;
};

/**
 * Convert feet to meters.
 * @param {number} ft - feet
 * @returns {number} meters
 */
export const ftToM = (ft) => {
    return ft / METERS_TO_FEET;
};

/**
 * Format a length string based on the active unit system
 * @param {number} meters - raw value in meters
 * @param {string} unitSystem - 'metric' | 'imperial'
 * @param {number} precision - decimal places
 * @returns {string} e.g. '1.50 m' or '4.92 ft'
 */
export const formatLength = (meters, unitSystem = 'metric', precision = 1) => {
    if (typeof meters !== 'number') return '';
    if (unitSystem === 'imperial') {
        return `${mToFt(meters).toFixed(precision)} ft`;
    }
    return `${meters.toFixed(precision)} m`;
};

/**
 * Format an area string based on the active unit system
 * @param {number} sqMeters - raw value in sq meters
 * @param {string} unitSystem - 'metric' | 'imperial'
 * @param {number} precision - decimal places
 * @returns {string} e.g. '25.0 m²' or '269.1 sq ft'
 */
export const formatArea = (sqMeters, unitSystem = 'metric', precision = 1) => {
    if (typeof sqMeters !== 'number') return '';
    if (unitSystem === 'imperial') {
        const sqFt = sqMeters * SQ_METERS_TO_SQ_FEET;
        return `${sqFt.toFixed(precision)} sq ft`;
    }
    return `${sqMeters.toFixed(precision)} m²`;
};

/**
 * Prepare a meter value for an input field
 * @param {number} meters
 * @param {string} unitSystem 
 * @returns {number}
 */
export const getDisplayValue = (meters, unitSystem = 'metric') => {
    if (unitSystem === 'imperial') {
        return mToFt(meters);
    }
    return meters;
};

/**
 * Convert a value strictly from the input back to meters
 * @param {number} inputValue 
 * @param {string} unitSystem 
 * @returns {number} meters
 */
export const getMetersFromInput = (inputValue, unitSystem = 'metric') => {
    const val = parseFloat(inputValue);
    if (unitSystem === 'imperial') {
        return ftToM(val);
    }
    return val;
};

/**
 * Get unit string
 */
export const getUnitLabel = (unitSystem = 'metric') => {
    return unitSystem === 'imperial' ? 'ft' : 'm';
};

export const getAreaUnitLabel = (unitSystem = 'metric') => {
    return unitSystem === 'imperial' ? 'sq ft' : 'm²';
};
